import React, { useEffect, useState } from "react";
import api from "@/auth/api";
import Pagination from "@/components/Pagination";
import PageSizeSelector from "@/components/PageSizeSelector";
import Loader from "@/components/Loader";
import { alertSuccess, alertError } from "@/components/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faSave,
  faXmark,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

function AgentList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [editRowId, setEditRowId] = useState(null);
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const fetchData = async (silent = false) => {
    if (!silent && data.length === 0) {
      setLoading(true);
    }
    try {
      const response = await api.get("/campaign/panel/agent/control", {
        params: {
          page: currentPage,
          page_size: pageSize,
        },
      });

      const items = response.data?.result || response.data || [];
      setData(Array.isArray(items) ? items : []);

      const pagination = response.data?.pagination || {};
      const total = pagination.total_pages || (pagination.count ? Math.ceil(pagination.count / pageSize) : 1);
      setTotalPages(total || 1);
    } catch (error) {
      console.error("Error fetching agent control data:", error);
      alertError("Failed to fetch agent control data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditRowId(row.id);
    setEditedData({ ...row });
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditedData({});
  };

  const handleToggleField = (field) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleTextChange = (e, field) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSave = async () => {
    // Keep a copy of the edited data to perform optimistic update
    const savedData = { ...editedData };
    try {
      // Optimistically update the UI list item immediately
      setData((prev) =>
        prev.map((item) => (item.id === savedData.id ? { ...item, ...savedData } : item))
      );
      setEditRowId(null);
      setEditedData({});

      const response = await api.patch("/campaign/panel/agent/control", savedData);
      alertSuccess(response?.data?.success || "Agent control updated successfully");

      // Quietly fetch actual server data in the background to ensure consistency
      fetchData(true);
    } catch (error) {
      console.error("Error updating agent control data:", error);
      alertError(error?.response?.data?.errors || "Failed to update agent control");
      // Revert the optimistic update on error by fetching again
      fetchData(true);
    }
  };

  // Dynamically extract all header keys from the first object (excluding 'id')
  const headers = data.length > 0 ? Object.keys(data[0]).filter((key) => key !== "id") : [];
  const editableFields = ["show_referral", "show_user", "show_elite_user"];

  if (loading && data.length === 0) {
    return (
      <div className="w-full flex justify-center p-6">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center p-6">
      <div className="w-full flex items-center flex-col">
        <div className="max-w-[100%] overflow-auto">
          <div className="tableContainer">
            <table className="table text-xs w-[88vw]" role="table">
              <thead className="stickyHeader bg-[var(--mainCol)] text-white">
                <tr>
                  <th className="p-2 border text-left">ID</th>
                  {headers.map((header) => (
                    <th key={header} className="p-2 border text-center">
                      {header.replace(/_/g, " ").toUpperCase()}
                    </th>
                  ))}
                  <th className="p-2 border text-center">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => {
                  const isEditing = editRowId === row.id;

                  return (
                    <tr key={row.id}>
                      <td className="p-3 border text-left font-medium">{row.id}</td>
                      {headers.map((header) => {
                        const val = isEditing ? editedData[header] : row[header];
                        const isBool = typeof val === "boolean";
                        const isEditable = editableFields.includes(header);

                        return (
                          <td key={header} className="p-3 border text-center">
                            {isEditing && isEditable ? (
                              isBool ? (
                                <div
                                  className="cursor-pointer inline-flex justify-center"
                                  onClick={() => handleToggleField(header)}
                                >
                                  <FontAwesomeIcon
                                    color={val ? "var(--green_Main)" : "indianred"}
                                    icon={val ? faCheck : faXmark}
                                    className="text-base"
                                  />
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  value={val ?? ""}
                                  onChange={(e) => handleTextChange(e, header)}
                                  className="p-1 border text-black min-w-[120px] rounded"
                                />
                              )
                            ) : isBool ? (
                              <FontAwesomeIcon
                                color={val ? "var(--green_Main)" : "indianred"}
                                icon={val ? faCheck : faXmark}
                                className="text-base"
                              />
                            ) : (
                              String(val ?? "")
                            )}
                          </td>
                        );
                      })}
                      <td className="p-3 border text-center">
                        {isEditing ? (
                          <div className="flex justify-center gap-2">
                            <FontAwesomeIcon
                              className="p-1 cursor-pointer hover:scale-110 transition-transform"
                              color="#67a97b"
                              onClick={handleSave}
                              icon={faSave}
                              title="Save"
                            />
                            <FontAwesomeIcon
                              className="p-1 cursor-pointer hover:scale-110 transition-transform"
                              color="indianred"
                              onClick={handleCancel}
                              icon={faXmark}
                              title="Cancel"
                            />
                          </div>
                        ) : (
                          <div className="flex justify-center">
                            <FontAwesomeIcon
                              className="cursor-pointer hover:scale-110 transition-transform"
                              onClick={() => handleEdit(row)}
                              icon={faPenToSquare}
                              title="Edit"
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={headers.length + 2} className="p-4 text-center text-gray-500">
                      No agent controls found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex mt-4 items-center justify-between w-11/12 max-w-[88vw]">
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
          />
          <PageSizeSelector pageSize={pageSize} setPageSize={setPageSize} />
        </div>
      </div>
    </div>
  );
}

export default AgentList;
